// React Imports
import { cloneElement, createElement, forwardRef } from 'react';
import type { ForwardRefRenderFunction } from 'react';

import { css } from '@emotion/react';
// Third-party Imports
import classnames from 'classnames';

// Type Imports
import type { ChildrenType, MenuButtonProps } from '../../types';

// Component Imports
import { RouterLink } from '../RouterLink';

import { useTheme } from '@mui/material';
import { NavLink } from 'react-router-dom';
// Util Imports
import { menuClasses } from '../../utils/menuClasses';

type MenuButtonStylesProps = Partial<ChildrenType> & {
  level: number;
  active?: boolean;
  disabled?: boolean;
  isCollapsed?: boolean;
  isPopoutWhenCollapsed?: boolean;
};

export const menuButtonStyles = (props: MenuButtonStylesProps) => {
  // Props
  const { level, disabled, children, isCollapsed, isPopoutWhenCollapsed } = props;
  const _theme = useTheme();
  return css({
    display: 'flex',
    alignItems: 'center',
    minBlockSize: `${level === 0 ? '50px' : '30px'}`,
    textDecoration: 'none',
    color: 'inherit',
    boxSizing: 'border-box',
    cursor: 'pointer',
    paddingInlineEnd: '20px',
    paddingInlineStart: `${level === 0 ? 20 : (isPopoutWhenCollapsed && isCollapsed ? level : level + 1) * 20}px`,

    // '&:hover, &[aria-expanded="true"]': {
    //   backgroundColor: `${theme.palette.primary.lighterOpacity} !important`,
    // },

    '&:focus-visible': {
      outline: 'none',
      backgroundColor: '#f3f3f3',
    },

    ...(disabled && {
      pointerEvents: 'none',
      cursor: 'default',
      color: '#adadad',
    }),

    // All the active styles are applied to the button including menu items or submenu
    [`&.${menuClasses.active}`]: {
      // ...(!children && { color: 'white' }),
      // backgroundColor: children ? '#f3f3f3' : '#765feb',
    },
  });
};

const MenuButton: ForwardRefRenderFunction<HTMLAnchorElement, MenuButtonProps> = (
  { className, component, children, ...rest },
  ref,
) => {
  if (component) {
    // If component is a string, create a new element of that type
    if (typeof component === 'string') {
      return createElement(
        component,
        {
          className: classnames(className),
          ...rest,
          ref,
        },
        children,
      );
    }
    // Otherwise, clone the element
    const { className: classNameProp, ...props } = component.props as any;

    return cloneElement(
      component,
      {
        className: classnames(className, classNameProp),
        ...rest,
        ...props,
        ref,
      },
      children,
    );
  }
  // If there is no component but href is defined, render RouterLink
  if (rest.href) {
    return (
      <RouterLink ref={ref} className={className} href={rest.href} {...rest}>
        {children}
      </RouterLink>
    );
  }
  return (
    <NavLink to={rest.href || ''} ref={ref} className={className} {...rest}>
      {children}
    </NavLink>
  );
};

export default forwardRef(MenuButton);
